(() => {
  if (customElements.get('infinite-scrolling-announcement')) return;

  class InfiniteScrollingAnnouncement extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.viewport = this.querySelector('[data-infinite-announcement-viewport]');
      this.track = this.querySelector('[data-infinite-announcement-track]');
      this.primaryGroup = this.querySelector('[data-infinite-announcement-group="primary"]');
      this.secondaryGroup = this.querySelector('[data-infinite-announcement-group="secondary"]');
      this.closeButton = this.querySelector('[data-infinite-announcement-close]');
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isDesignMode = this.dataset.designMode === 'true';
      this.isInitialized = true;

      this.handleClose = this.dismiss.bind(this);
      this.handleMediaChange = this.scheduleLayout.bind(this);
      this.handleAssetLoad = this.handleAssetLoad.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);

      this.closeButton?.addEventListener('click', this.handleClose);

      if (this.isDismissed() && !this.isDesignMode) {
        this.hidden = true;
        return;
      }

      if (!this.viewport || !this.track || !this.primaryGroup || !this.secondaryGroup) return;

      this.primaryItems = Array.from(
        this.primaryGroup.querySelectorAll(':scope > [data-infinite-announcement-item]')
      );
      this.secondaryItems = Array.from(
        this.secondaryGroup.querySelectorAll(':scope > [data-infinite-announcement-item]')
      );

      if (this.primaryItems.length === 0) return;

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.handleMediaChange);
      }
      this.mobileMedia.addEventListener('change', this.handleMediaChange);
      this.reducedMotionMedia.addEventListener('change', this.handleMediaChange);
      this.addEventListener('load', this.handleAssetLoad, true);

      if (this.isDesignMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }

      this.scheduleLayout();
      document.fonts?.ready.then(() => this.scheduleLayout());
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      window.cancelAnimationFrame(this.layoutFrame);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.handleMediaChange);
      this.closeButton?.removeEventListener('click', this.handleClose);
      this.mobileMedia?.removeEventListener('change', this.handleMediaChange);
      this.reducedMotionMedia?.removeEventListener('change', this.handleMediaChange);
      this.removeEventListener('load', this.handleAssetLoad, true);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      this.isInitialized = false;
    }

    scheduleLayout() {
      if (!this.isConnected || !this.primaryGroup) return;

      window.cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = window.requestAnimationFrame(() => this.updateLayout());
    }

    updateLayout() {
      this.removeFillItems();

      if (!this.shouldAnimate()) {
        this.style.removeProperty('--infinite-announcement-duration');
        return;
      }

      const minimumRepetitions = this.getClampedNumber(this.dataset.minimumRepetitions, 1, 10, 1);
      let repetitions = 1;
      let safetyCounter = 0;

      while (
        (repetitions < minimumRepetitions || this.primaryGroup.scrollWidth < this.viewport.clientWidth) &&
        safetyCounter < 50
      ) {
        this.appendSequence(this.primaryGroup, this.primaryItems);
        this.appendSequence(this.secondaryGroup, this.secondaryItems);
        repetitions += 1;
        safetyCounter += 1;
      }

      const distance = this.primaryGroup.getBoundingClientRect().width;
      const configuredSpeed = this.mobileMedia.matches ? this.dataset.speedMobile : this.dataset.speedDesktop;
      const speed = this.getClampedNumber(configuredSpeed, 10, 200, 60);
      const duration = Math.max(distance / speed, 1);

      this.style.setProperty('--infinite-announcement-duration', `${duration.toFixed(3)}s`);
    }

    shouldAnimate() {
      if (this.dataset.animate !== 'true') return false;
      if (this.reducedMotionMedia.matches) return false;
      if (this.mobileMedia.matches && this.dataset.disableAnimationMobile === 'true') return false;
      return true;
    }

    appendSequence(group, sourceItems) {
      sourceItems.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-infinite-announcement-fill', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
        group.append(clone);
      });
    }

    removeFillItems() {
      this.querySelectorAll('[data-infinite-announcement-fill]').forEach((item) => item.remove());
    }

    handleAssetLoad(event) {
      if (event.target instanceof HTMLImageElement) this.scheduleLayout();
    }

    handleBlockSelect(event) {
      const blockId = String(event.detail?.blockId || '');
      const item = this.primaryGroup?.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);

      if (!item) return;
      this.classList.add('is-editor-paused');
    }

    handleBlockDeselect(event) {
      const blockId = String(event.detail?.blockId || '');
      const item = this.primaryGroup?.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);

      if (item) this.classList.remove('is-editor-paused');
    }

    dismiss() {
      this.storeDismissal();
      this.hidden = true;
    }

    isDismissed() {
      if (this.dataset.dismissible !== 'true') return false;

      try {
        return this.getStorage()?.getItem(this.storageKey) === 'true';
      } catch (_error) {
        return false;
      }
    }

    storeDismissal() {
      if (this.dataset.dismissible !== 'true' || this.isDesignMode) return;

      try {
        this.getStorage()?.setItem(this.storageKey, 'true');
      } catch (_error) {
        // Die Leiste bleibt auch nutzbar, wenn der Browserspeicher blockiert ist.
      }
    }

    getStorage() {
      return this.dataset.storage === 'persistent' ? window.localStorage : window.sessionStorage;
    }

    get storageKey() {
      return `infinite-scrolling-announcement:${this.dataset.sectionId}:dismissed`;
    }

    getClampedNumber(value, minimum, maximum, fallback) {
      const parsedValue = Number.parseFloat(value);

      if (Number.isNaN(parsedValue)) return fallback;
      return Math.min(Math.max(parsedValue, minimum), maximum);
    }
  }

  customElements.define('infinite-scrolling-announcement', InfiniteScrollingAnnouncement);
})();
